/**
 * HLOS-168-B + Hotfix Safari: Storage factory — seleciona o backend
 * apropriado baseado no runtime atual.
 *
 * Lógica de seleção (em ordem de prioridade):
 * 1. **CapacitorIOSBackend** — se iOS nativo (Capacitor.isNativePlatform()
 *    + getPlatform() === "ios") E IOSVaultBridge está disponível.
 * 2. **OPFSBackend** — se `navigator.storage.getDirectory()` existe
 *    E responde sem travar (Safari hotfix: probe com timeout).
 * 3. **IndexedDBBackend** (NOVO) — fallback universal, sempre disponível.
 * 4. **MemoryBackend** — fallback para testes / SSR.
 *
 * Hotfix Safari (2026-09-08): Safari em alguns contextos trava o
 * `getDirectory()` indefinidamente. A factory assíncrona faz probe com
 * timeout 500ms; se travar, cai no IndexedDB.
 *
 * MemoryBackend NUNCA deve ser selecionado em produção (CL-07 violation).
 *
 * Constitucional:
 * - CL-07 (vault local-first): seleciona backend local sempre.
 * - CL-08 (zero key escrow): bridge NUNCA envia chave (apenas bytes cifrados).
 * - Não toca LLM, LATENT_DIM, P=1000.
 */

import type { VaultStorage } from "./store";
import { OPFSBackend } from "./backends/opfs";
import { IndexedDBBackend } from "./backends/indexeddb";
import { CapacitorIOSBackend, isCapacitorIOSAvailable } from "./backends/capacitor-ios";
import { MemoryBackend } from "./backends/memory";

/** ID do backend selecionado. */
export type VaultBackendId = "opfs" | "indexeddb" | "capacitor-ios" | "memory";

/**
 * Cache do probe assíncrono do OPFS. Se Safari travar, este cache nunca
 * resolve, e o factory assíncrono cai no IndexedDB.
 */
let opfsProbeCache: Promise<boolean> | null = null;

/**
 * Probe do OPFS com timeout de 500ms. Retorna true se OPFS responde
 * normalmente, false se travar ou lançar erro.
 */
function probeOPFS(): Promise<boolean> {
  if (opfsProbeCache) return opfsProbeCache;
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) {
    opfsProbeCache = Promise.resolve(false);
    return opfsProbeCache;
  }
  opfsProbeCache = new Promise<boolean>((resolve) => {
    const timeout = setTimeout(() => {
      console.warn("[HLOS Vault] OPFS probe timeout (500ms) — usando IndexedDB");
      resolve(false);
    }, 500);
    navigator.storage
      .getDirectory()
      .then(() => {
        clearTimeout(timeout);
        resolve(true);
      })
      .catch((e: unknown) => {
        clearTimeout(timeout);
        console.warn(
          `[HLOS Vault] OPFS probe failed: ${e instanceof Error ? e.name : String(e)} — usando IndexedDB`,
        );
        resolve(false);
      });
  });
  return opfsProbeCache;
}

/**
 * Factory ASSÍNCRONA: cria o backend apropriado baseado no runtime.
 * Faz probe de OPFS com timeout (Safari hotfix).
 *
 * Returns:
 *   Promise<VaultStorage> — backend selecionado.
 */
export async function storageFactoryAsync(): Promise<VaultStorage> {
  if (isCapacitorIOSAvailable()) {
    return new CapacitorIOSBackend();
  }
  const opfsWorks = await probeOPFS();
  if (opfsWorks) {
    return new OPFSBackend(() => navigator.storage.getDirectory());
  }
  if (typeof indexedDB !== "undefined") {
    return new IndexedDBBackend();
  }
  // Fallback: testes / SSR / runtime sem storage algum.
  return new MemoryBackend();
}

/** Factory síncrona (legacy) — para compat. Detecta OPFS sem probe. */
export function storageFactory(): VaultStorage {
  if (isCapacitorIOSAvailable()) {
    return new CapacitorIOSBackend();
  }
  if (typeof navigator !== "undefined" && navigator.storage?.getDirectory) {
    return new OPFSBackend(() => navigator.storage.getDirectory());
  }
  if (typeof indexedDB !== "undefined") {
    return new IndexedDBBackend();
  }
  // Fallback: testes / SSR / runtime sem storage algum.
  return new MemoryBackend();
}

/** ID legível do backend (para logs / debug). */
export function getBackendId(backend: VaultStorage): VaultBackendId {
  if (backend instanceof CapacitorIOSBackend) return "capacitor-ios";
  if (backend instanceof OPFSBackend) return "opfs";
  if (backend instanceof IndexedDBBackend) return "indexeddb";
  if (backend instanceof MemoryBackend) return "memory";
  // Fallback genérico (ex: mock em testes) — conservador, marca como memory.
  return "memory";
}
